// iter150AZ.26 — Add PM Item picker for QuoteDetailPage.
//
// Flow:
//   1. Lists the customer's compressors (Products_By_Serial).
//      If only one compressor exists, the picker auto-selects it and
//      jumps straight to the PM-parts view.
//   2. For the selected compressor, fetches
//      ``GET /api/compressors/{serial_id}/service-forecast`` which
//      already returns: items[] with bucket ∈ {due_now, next_service,
//      future, complete}, qty_per_service, units_short (overdue),
//      hours_until, units_purchased, next_service_at_hour, unit_price.
//   3. Renders 3 groups in this strict order:
//      • Missing / overdue (bucket=due_now OR units_short > 0)   — RED
//      • Service (bucket=next_service OR future)                 — AMBER
//      • Other (bucket=complete)                                 — GREY
//   4. Multi-select; on confirm, inserts one Quote_Detail line per
//      selected part with ``Related to Product SN`` populated to the
//      compressor's serial number, qty = qty_per_service, price =
//      unit_price (from the part's List_Price / List_Price_CA).
//
// Per user spec (iter150AZ.26): batch insert in one click (no
// per-line modal).
import React, { useEffect, useMemo, useState } from "react";
import { X as XIcon, Spinner, Plus, CheckSquare, Square, WarningCircle, Wrench, CheckCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { addQuoteLine } from "../lib/api";
import { formatMoney } from "../lib/cart";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

// ─── helpers ──────────────────────────────────────────────────────
function _classifyBucket(item) {
  // iter150AZ.30 — Trust the backend's ``bucket`` value as-is. The
  // service-forecast endpoint now sets ``bucket=due_now`` whenever
  // ``Service_Table_by_SN._Missing`` is non-zero, so we no longer
  // need to second-guess it from ``units_short``. Mapping:
  //   * ``due_now``        → "missing"  (rose group)
  //   * ``future`` / ``next_service`` → "service" (amber group)
  //   * ``complete``       → "other"   (grey group)
  if (item.bucket === "due_now") return "missing";
  if (item.bucket === "next_service" || item.bucket === "future") return "service";
  return "other";
}

function _formatHourSummary(item, currentHours, t) {
  const hoursUntil = item.hours_until;
  const nextAt = item.next_service_at_hour;
  if (hoursUntil == null) {
    return t("pm_picker.no_interval", "No hour-based interval");
  }
  if (hoursUntil <= 0) {
    const overBy = (currentHours ?? 0) - (nextAt ?? 0);
    if (overBy > 0) {
      return t("pm_picker.overdue_by", "Overdue by {{n}} hr", { n: overBy });
    }
    return t("pm_picker.due_now", "Due now @ {{n}} hr", { n: nextAt });
  }
  return t("pm_picker.due_in", "Due in {{n}} hr (at {{at}})",
           { n: hoursUntil, at: nextAt });
}

// ─── compressor row ───────────────────────────────────────────────
function CompressorRow({ c, onPick }) {
  const t = useT();
  return (
    <button type="button"
            data-testid={`pm-picker-compressor-${c.serial_number || c.serial_id}`}
            onClick={() => onPick(c)}
            className="w-full text-left px-4 py-3 border-b last:border-b-0 border-slate-100
                       hover:bg-blue-50 flex items-center gap-3 transition">
      <div className="flex-1 min-w-0">
        <div className="font-mono font-bold text-slate-900 text-sm">
          SN {c.serial_number || "—"}
        </div>
        <div className="text-[12px] text-slate-600 truncate">
          {c.product_name || c.product_type || "—"}
        </div>
        {(c.hours != null || c.date_delivered) && (
          <div className="text-[10px] text-slate-400 mt-0.5">
            {c.hours != null && <>{t("pm_picker.run_hours", "Hours")}: {c.hours}</>}
            {c.hours != null && c.date_delivered && " · "}
            {c.date_delivered && <>{t("pm_picker.delivered", "Delivered")}: {String(c.date_delivered).slice(0, 10)}</>}
          </div>
        )}
      </div>
      <Plus size={14} weight="bold" className="text-blue-600" />
    </button>
  );
}

// ─── PM part row ──────────────────────────────────────────────────
function PmPartRow({ item, group, selected, onToggle, currency, currentHours }) {
  const t = useT();
  const disabled = !item.product_code;   // no part SKU resolved
  const ringClass =
    group === "missing" ? "border-rose-200 bg-rose-50/40"
    : group === "service" ? "border-amber-200 bg-amber-50/40"
    : "border-slate-200 bg-slate-50/40";
  const badgeClass =
    group === "missing" ? "bg-rose-600 text-white"
    : group === "service" ? "bg-amber-500 text-white"
    : "bg-slate-400 text-white";
  const badgeLabel =
    group === "missing" ? t("pm_picker.badge_missing", "MISSING")
    : group === "service" ? t("pm_picker.badge_service", "PM")
    : t("pm_picker.badge_other", "OK");
  const hourLine = _formatHourSummary(item, currentHours, t);
  const unitPrice = Number(item.unit_price || 0);
  const qty = Number(item.units_short || 0) > 0
    ? Number(item.units_short)
    : Number(item.qty_per_service || 1) || 1;
  const lineTotal = unitPrice * qty;
  return (
    <label className={`flex items-start gap-3 px-3 py-2.5 border ${ringClass}
                       cursor-pointer hover:brightness-95 transition
                       ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
           data-testid={`pm-picker-part-${item.product_code || item.id}`}>
      <input type="checkbox"
             className="mt-1 h-4 w-4 accent-blue-600"
             checked={selected}
             disabled={disabled}
             onChange={() => onToggle(item)}
             data-testid={`pm-picker-part-checkbox-${item.product_code || item.id}`}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${badgeClass}`}>
            {badgeLabel}
          </span>
          <span className="text-sm font-semibold text-slate-900 truncate">
            {item.part_name || item.service_type || "—"}
          </span>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[10px] text-slate-600 font-mono">
          {item.product_code && <span>{item.product_code}</span>}
          <span className="font-sans">
            {t("pm_picker.qty_label", "Qty needed")}: <strong>{qty} {item.unit_hint || ""}</strong>
          </span>
          <span className={`font-sans ${group === "missing" ? "text-rose-700 font-bold" : "text-slate-600"}`}>
            {hourLine}
          </span>
        </div>
        {item.description && (
          <div className="text-[10px] text-slate-500 truncate mt-0.5 italic">
            {item.description}
          </div>
        )}
      </div>
      <div className="text-right whitespace-nowrap">
        <div className={`text-sm font-mono font-bold ${unitPrice > 0 ? "text-slate-900" : "text-rose-600"}`}>
          {formatMoney(unitPrice, currency)}
        </div>
        {selected && unitPrice > 0 && (
          <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">
            = {formatMoney(lineTotal, currency)}
          </div>
        )}
      </div>
    </label>
  );
}

// ─── main modal ───────────────────────────────────────────────────
export default function PmItemPickerModal({
  open, onClose, headingId, customerId, currency, onAdded,
}) {
  const t = useT();
  const [compressors, setCompressors] = useState([]);
  const [loadingCompressors, setLoadingCompressors] = useState(false);
  const [selectedCompressor, setSelectedCompressor] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});  // {id: item}
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  // Load compressors once when opened
  useEffect(() => {
    if (!open || !customerId) return;
    let cancelled = false;
    setLoadingCompressors(true);
    setError("");
    setSelectedCompressor(null);
    setForecast(null);
    setSelectedItems({});
    (async () => {
      try {
        const r = await api.get(`/customers/${encodeURIComponent(customerId)}/compressors`);
        if (cancelled) return;
        const items = (r.data?.items || []).filter(c =>
          // Only include actual final products (compressors/dryers/tanks)
          // that have a serial_id; everything else can't have a PM schedule.
          c.serial_id && (c.product_type || c.product_name)
        );
        setCompressors(items);
        // Auto-select if exactly one compressor is owned.
        if (items.length === 1) {
          setSelectedCompressor(items[0]);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load compressors");
          setCompressors([]);
        }
      } finally {
        if (!cancelled) setLoadingCompressors(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, customerId]);

  // Load PM forecast once a compressor is selected
  useEffect(() => {
    if (!selectedCompressor) { setForecast(null); return; }
    let cancelled = false;
    setLoadingForecast(true);
    setError("");
    setSelectedItems({});
    (async () => {
      try {
        const r = await api.get(
          `/compressors/${encodeURIComponent(selectedCompressor.serial_id)}/service-forecast`,
        );
        if (!cancelled) setForecast(r.data || null);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Could not load PM forecast");
          setForecast(null);
        }
      } finally {
        if (!cancelled) setLoadingForecast(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCompressor]);

  // Group items: missing > service > other
  const groups = useMemo(() => {
    const out = { missing: [], service: [], other: [] };
    for (const it of (forecast?.items || [])) {
      const g = _classifyBucket(it);
      out[g].push(it);
    }
    // Sort missing by units_short DESC then by hours_until ASC (most urgent first).
    out.missing.sort((a, b) =>
      Number(b.units_short || 0) - Number(a.units_short || 0)
      || Number(a.hours_until ?? 9e9) - Number(b.hours_until ?? 9e9),
    );
    // Service: by hours_until ASC.
    out.service.sort((a, b) =>
      Number(a.hours_until ?? 9e9) - Number(b.hours_until ?? 9e9),
    );
    return out;
  }, [forecast]);

  const selectedCount = Object.keys(selectedItems).length;
  const selectedTotal = useMemo(() => {
    let total = 0;
    for (const it of Object.values(selectedItems)) {
      const unitPrice = Number(it.unit_price || 0);
      const qty = Number(it.units_short || 0) > 0
        ? Number(it.units_short)
        : Number(it.qty_per_service || 1) || 1;
      total += unitPrice * qty;
    }
    return total;
  }, [selectedItems]);

  const toggle = (item) => {
    if (!item.product_code) return;
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = item;
      return next;
    });
  };

  const selectGroupAll = (g) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      for (const it of groups[g]) {
        if (it.product_code) next[it.id] = it;
      }
      return next;
    });
  };

  const submit = async () => {
    if (selectedCount === 0) return;
    setAdding(true);
    setError("");
    const serialNumber = selectedCompressor?.serial_number || "";
    let okCount = 0;
    const failures = [];
    for (const it of Object.values(selectedItems)) {
      const unitPrice = Number(it.unit_price || 0);
      const qty = Number(it.units_short || 0) > 0
        ? Math.ceil(Number(it.units_short))
        : Number(it.qty_per_service || 1) || 1;
      try {
        await addQuoteLine(headingId, {
          product_code: it.product_code,
          quantity: qty,
          price: unitPrice,
          discount: 0,
          explanation: it.part_name || it.service_type || "",
          product_type: "Part",
          related_to_product_sn: serialNumber,
        });
        okCount += 1;
      } catch (e) {
        failures.push({ name: it.part_name, detail: e?.response?.data?.detail || e?.message });
      }
    }
    setAdding(false);
    if (okCount > 0) {
      onAdded && onAdded();
      toast.success(
        t("pm_picker.toast_added",
          "Added {{n}} PM line{{s}} to the quote.",
          { n: okCount, s: okCount === 1 ? "" : "s" }),
        { description: failures.length ? t("pm_picker.toast_partial",
                                           "{{n}} could not be added — see details.",
                                           { n: failures.length }) : undefined },
      );
    }
    if (failures.length > 0) {
      setError(
        t("pm_picker.batch_partial_error",
          "{{n}} part{{s}} could not be added: ",
          { n: failures.length, s: failures.length === 1 ? "" : "s" })
        + failures.map(f => f.name).join(", "),
      );
    }
    if (failures.length === 0) {
      // Clean exit only when everything inserted.
      onClose && onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="pm-picker-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-sm shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">
              {t("pm_picker.title", "Add PM Item")}
            </h2>
            <p className="text-[11px] text-slate-500">
              {selectedCompressor
                ? t("pm_picker.subtitle_compressor",
                    "Compressor SN {{sn}} · {{name}}",
                    { sn: selectedCompressor.serial_number, name: selectedCompressor.product_name || "" })
                : t("pm_picker.subtitle_pick", "Pick the compressor to load its PM schedule")}
            </p>
          </div>
          <button type="button"
                  onClick={onClose}
                  data-testid="pm-picker-close"
                  className="text-slate-400 hover:text-slate-700">
            <XIcon size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {error && (
            <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 text-[11px] rounded-sm flex items-start gap-2"
                 data-testid="pm-picker-error">
              <WarningCircle size={14} weight="bold" className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!selectedCompressor && (
            <>
              {loadingCompressors && (
                <div className="text-[12px] text-slate-500 inline-flex items-center gap-2">
                  <Spinner size={13} weight="bold" className="animate-spin" />
                  {t("pm_picker.loading_compressors", "Loading compressors…")}
                </div>
              )}
              {!loadingCompressors && compressors.length === 0 && (
                <div className="text-[12px] text-slate-600 py-6 text-center"
                     data-testid="pm-picker-empty-compressors">
                  {t("pm_picker.no_compressors",
                     "This customer doesn't own any registered compressors.")}
                </div>
              )}
              {!loadingCompressors && compressors.length > 0 && (
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  {compressors.map(c => (
                    <CompressorRow key={c.serial_id} c={c} onPick={setSelectedCompressor} />
                  ))}
                </div>
              )}
            </>
          )}

          {selectedCompressor && loadingForecast && (
            <div className="text-[12px] text-slate-500 inline-flex items-center gap-2 py-6">
              <Spinner size={13} weight="bold" className="animate-spin" />
              {t("pm_picker.loading_pm", "Loading PM schedule…")}
            </div>
          )}

          {selectedCompressor && !loadingForecast && forecast && (
            <>
              {/* Run-hours summary */}
              <div className="mb-3 px-3 py-2 rounded-sm bg-slate-50 border border-slate-200
                              flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-700"
                   data-testid="pm-picker-summary">
                <div>
                  <span className="text-slate-400 uppercase tracking-wider text-[9px]">
                    {t("pm_picker.run_hours", "Hours")}:
                  </span>
                  <span className="ml-1 font-mono font-bold text-slate-900">
                    {forecast.summary?.current_hours ?? "—"}
                  </span>
                </div>
                {forecast.summary?.next_service_at_hour && (
                  <div>
                    <span className="text-slate-400 uppercase tracking-wider text-[9px]">
                      {t("pm_picker.next_service_at", "Next PM at")}:
                    </span>
                    <span className="ml-1 font-mono font-bold text-slate-900">
                      {forecast.summary.next_service_at_hour}h
                    </span>
                    <span className="ml-1 text-slate-500">
                      ({t("pm_picker.in_hrs", "in {{n}}h", { n: forecast.summary.hours_until_next })})
                    </span>
                  </div>
                )}
                {compressors.length > 1 && (
                  <button type="button"
                          data-testid="pm-picker-change-compressor"
                          onClick={() => setSelectedCompressor(null)}
                          className="ml-auto text-[10px] uppercase tracking-wider text-blue-700 hover:text-blue-900 font-bold">
                    ← {t("pm_picker.change_compressor", "Change compressor")}
                  </button>
                )}
              </div>

              {/* Groups */}
              {["missing", "service", "other"].map((g) => {
                const list = groups[g];
                if (!list || list.length === 0) return null;
                const title =
                  g === "missing" ? t("pm_picker.group_missing", "Missing / Overdue")
                  : g === "service" ? t("pm_picker.group_service", "Upcoming PM parts")
                  : t("pm_picker.group_other", "Other (already serviced)");
                const Icon = g === "missing" ? WarningCircle : g === "service" ? Wrench : CheckCircle;
                const titleClass =
                  g === "missing" ? "text-rose-900"
                  : g === "service" ? "text-amber-900"
                  : "text-slate-600";
                return (
                  <div key={g} className="mb-3" data-testid={`pm-picker-group-${g}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${titleClass}`}>
                        <Icon size={13} weight="bold" />
                        {title}
                        <span className="text-slate-400 font-normal">({list.length})</span>
                      </h3>
                      {g !== "other" && (
                        <button type="button"
                                onClick={() => selectGroupAll(g)}
                                data-testid={`pm-picker-select-all-${g}`}
                                className="text-[10px] uppercase tracking-wider text-blue-700 hover:text-blue-900 font-bold">
                          {t("pm_picker.select_all", "Select all")}
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {list.map(it => (
                        <PmPartRow key={it.id || it.product_code}
                                   item={it}
                                   group={g}
                                   selected={!!selectedItems[it.id]}
                                   onToggle={toggle}
                                   currency={currency}
                                   currentHours={forecast.summary?.current_hours} />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* All groups empty */}
              {groups.missing.length === 0 && groups.service.length === 0 && groups.other.length === 0 && (
                <div className="text-[12px] text-slate-600 py-6 text-center"
                     data-testid="pm-picker-empty-pm">
                  {t("pm_picker.no_pm_schedule",
                     "No PM schedule is registered for this compressor. Use the regular Add Item button.")}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/60">
          <div className="text-[11px] text-slate-600">
            {selectedCount > 0 ? (
              <>
                <span className="font-bold text-slate-900">
                  {selectedCount} {t("pm_picker.selected", "selected")}
                </span>
                {selectedTotal > 0 && (
                  <span className="ml-2 font-mono">
                    = {formatMoney(selectedTotal, currency)}
                  </span>
                )}
              </>
            ) : (
              <span className="text-slate-400 italic">
                {t("pm_picker.none_selected", "No parts selected yet")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button"
                    onClick={onClose}
                    disabled={adding}
                    data-testid="pm-picker-cancel"
                    className="h-9 px-3 text-[10px] uppercase font-bold tracking-wider rounded-sm
                               border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-60">
              {t("action.cancel", "Cancel")}
            </button>
            <button type="button"
                    onClick={submit}
                    disabled={adding || selectedCount === 0}
                    data-testid="pm-picker-submit"
                    className="h-9 px-4 inline-flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider rounded-sm
                               bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
              {adding ? (
                <><Spinner size={12} weight="bold" className="animate-spin" />
                  {t("pm_picker.adding", "Adding…")}</>
              ) : (
                <><Plus size={12} weight="bold" />
                  {t("pm_picker.add_n_lines",
                     "Add {{n}} line{{s}}",
                     { n: selectedCount, s: selectedCount === 1 ? "" : "s" })}</>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
